import { useOverview } from '@hooks/useOverview';
import useTranslations from '@hooks/useTranslations';
import { getActivityPages } from '@lib/prepr/activity';
import Filters from '@molecules/Filters/Filters';
import CardGrid from '@organisms/CardGrid/CardGrid';
import Hero from '@organisms/Hero/Hero';
import Sections from '@organisms/Sections/Sections';
import { FilterGroup } from '@type-declarations/filters';
import { ActivityOverviewPage } from '@type-declarations/page';
import { groupCategoryFilters } from '@utils/filters/categoryFilter';
import { tagToFilter } from '@utils/filters/tagFilter';
import { useRef } from 'react';

interface Props {
  page: ActivityOverviewPage;
}

function ActivityOverviewTemplate({ page }: Props) {
  const t = useTranslations();

  const scrollToRef = useRef<HTMLDivElement | null>(null);

  const {
    title,
    introText,
    image,
    items: initItems,
    total: initTotal,
    alignment,
    highlightedTag,
  } = page;
  const {
    total,
    items,
    activeFilters,
    clearFilters,
    handleChange,
    currentPage,
    totalPages,
    hasNextPage,
    hasPrevPage,
    handleNextPage,
    handlePrevPage,
    handleGoToPage,
    loading,
  } = useOverview({
    initItems,
    initTotal,
    scrollToRef,
    fetch: getActivityPages,
  });

  const categoryGroups: FilterGroup[] = groupCategoryFilters(page.categories);

  const primaryTags = tagToFilter(page.primaryTags);

  return (
    <>
      <Hero
        title={title}
        text={introText}
        image={image}
        colorVariant="gray"
        alignment={alignment}
      />

      {(!!page.primaryTags?.length || !!page.extraTags?.length) && (
        <Filters
          highlightedTag={highlightedTag}
          primary={[
            {
              key: 'tags',
              filters: primaryTags,
            },
          ]}
          secondary={[
            ...categoryGroups,
            {
              title: t.tags,
              key: 'tags',
              filters: [...primaryTags, ...tagToFilter(page.extraTags)],
            },
          ]}
          activeFilters={activeFilters}
          searchLabel={t.locationSearch}
          onChange={handleChange}
          onClear={clearFilters}
        />
      )}

      <CardGrid
        total={total}
        loading={loading}
        notFoundTitle={t.locationNothingFoundTitle}
        notFoundText={t.locationNothingFoundText}
        items={items}
        scrollToRef={scrollToRef}
        currentPage={currentPage}
        totalPages={totalPages}
        hasPrevPage={hasPrevPage}
        hasNextPage={hasNextPage}
        handlePrevPage={handlePrevPage}
        handleNextPage={handleNextPage}
        handleGoToPage={handleGoToPage}
      />

      <Sections sections={page.sections} />
    </>
  );
}

export default ActivityOverviewTemplate;
